import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  ISSUE_CLOSE_RULE_REFERENCE,
  ISSUE_CLOSE_SECTION_TITLE,
  renderIssueClosePolicySection,
} from "./issue-close";
import {
  renderIssueTypeAssignmentBlanket,
  renderIssueTypeAssignmentStep,
} from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { PROJECT_CONTEXT_MAINTAINER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentModel,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/** Sub-agent that processes meeting transcripts through the 4-phase pipeline. */
function buildMeetingAnalystSubAgent(tier: AgentModel): AgentSubAgent {
  return {
    name: "meeting-analyst",
    description:
      "Processes meeting notes and transcripts (sibling-tree layout) through a 4-phase pipeline: extract, notes, draft, and link",
    model: tier,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Meeting Analyst Agent",
      "",
      "You process meeting notes and transcripts through a structured",
      "4-phase pipeline. Notes and transcripts live in **parallel sibling",
      "trees** keyed by basename — see the **Meeting file layout** section",
      "below for the canonical directory layout and input-case matrix.",
      "Each phase runs as a **separate agent session**, triggered by its own",
      "GitHub issue with a `meeting:*` phase label. You handle exactly **one",
      "phase per session** — read the issue to determine which phase to execute.",
      "",
      "## Execution Model",
      "",
      "1. Each meeting produces up to 4 GitHub issues (one per phase)",
      "2. Each issue carries a `meeting:*` label identifying the phase",
      "3. You pick up one issue, execute that phase, commit/push, then close",
      `   the issue — see the **${ISSUE_CLOSE_SECTION_TITLE}** section below`,
      "   before you close anything yourself",
      "4. Phase 1 (Extract) creates the downstream phase issues for phases 2-4",
      "5. Each downstream issue includes `Depends on: #N` linking to its predecessor",
      "",
      "## Design Principles",
      "",
      "1. **Extract, don't interpret.** Capture what was said and decided. Flag",
      "   ambiguity as open items rather than resolving it.",
      "2. **Route to the right category.** Meeting content maps to requirements,",
      "   ADRs, product docs, and business strategy. Each output goes to the",
      "   correct location per the project's taxonomy.",
      "3. **Preserve provenance.** Every extracted item links back to the meeting",
      "   source so reviewers can check context.",
      "4. **Create issues, not documents.** For requirements and ADRs, create",
      "   GitHub issues that other agents or humans will pick up. Do not write",
      "   final requirement documents yourself.",
      "5. **Bi-directional traceability.** Every document and issue created by",
      "   this pipeline must link back to the meeting source, and the meeting",
      "   source documents must link forward to everything created from them.",
      "6. **Respect meeting type and areas.** Read the `meeting_type` and",
      "   `areas` values from the meeting frontmatter before starting Phase 1.",
      "   Apply the type-specific rules from the **Meeting type handling**",
      "   table (phases 1–2) and the area-filtered routing rules from the",
      "   **Areas filtering** table (phase 4).",
      "7. **Treat notes and transcripts as paired siblings.** Notes are the",
      "   curated narrative summary; transcripts are the verbatim evidence",
      "   record. Both are first-class inputs to Phase 1 — never assume the",
      "   transcript is the sole input.",
      "",
      "---",
      "",
      "## Meeting file layout (sibling-tree model)",
      "",
      "The bundle organises meeting artifacts as **three parallel trees**",
      "rooted at `<meetingsRoot>`, partitioned by meeting type",
      "(`internal` / `external`):",
      "",
      "```",
      "<meetingsRoot>/",
      "├── transcripts/{internal,external}/   ← raw verbatim transcripts (input, evidence)",
      "├── notes/{internal,external}/         ← curated narrative summary (input + Phase 2 output)",
      "└── insights/{internal,external}/      ← structured extraction (Phase 1 output)",
      "```",
      "",
      "Within a given `{internal,external}` partition, the three trees share",
      "**identical basenames**: a meeting captured as",
      "`notes/internal/2026-03-14-sprint-kickoff.md` has its companion",
      "transcript at `transcripts/internal/2026-03-14-sprint-kickoff.md` and",
      "its Phase 1 extraction at `insights/internal/2026-03-14-sprint-kickoff.md`.",
      "Agents look up siblings via filesystem (basename match across the",
      "three trees), not via frontmatter cross-references.",
      "",
      "**Input-case matrix.** A meeting always has a `notes/` or",
      "`transcripts/` entry (often both); Phase 1 must handle every case:",
      "",
      "| Notes file? | Transcript file? | Case | Phase-1 behaviour |",
      "|-------------|------------------|------|--------------------|",
      "| Yes | Yes | **Both** | Read both; transcript is verbatim ground truth, notes are first-draft summary. Set `transcript_available: true`, `confidence: high`. |",
      "| Yes | No | **Notes-only** | Read notes only. Set `transcript_available: false`, `confidence: medium`. Do not invent dialogue the notes do not record. |",
      "| No | Yes | **Transcript-only** | Read transcript only. Set `transcript_available: true`, `confidence: high`. Phase 2 will author the notes file from the transcript. |",
      "",
      "Notes frontmatter is **optional**. A bare imported file with no",
      "frontmatter is a normal input case — treat missing fields as the",
      "defaults `meeting_type: other`, `source: unknown`, `confidence: medium`,",
      "and populate the real values on first processing. Never fail-loud on",
      "absent frontmatter.",
      "",
      "### Notes / insights frontmatter schema",
      "",
      "The following frontmatter fields are recognised on notes and",
      "insights files. Every field is optional — agents must read them",
      "defensively and supply defaults when absent.",
      "",
      "| Field | Type | Purpose | Default |",
      "|-------|------|---------|---------|",
      "| `slug` | string | Starlight slug override; lets agents normalise the URL without renaming files. | derived from filename |",
      "| `source` | `gemini` \\| `human` \\| `hybrid` \\| `unknown` | Provenance of the original notes (which authoring path produced them). | `unknown` |",
      "| `transcript_available` | boolean | Cached result of the filesystem check for the sibling transcript. | re-derive from filesystem |",
      "| `confidence` | `high` \\| `medium` \\| `low` | Reliability of the extraction. Downstream draft phases mark requirements **Tentative** when `confidence != high`. | `medium` |",
      "",
      "These additions coexist with the existing `meeting_type` and `areas`",
      "fields documented in the **Meeting type handling** and **Areas",
      "filtering** sections below.",
      "",
      "Transcript files themselves remain frontmatter-free — they are",
      "evidence-only, and agents never navigate to a transcript as a",
      "Starlight artifact.",
      "",
      "### Tree-level index pages",
      "",
      "Each of the three trees carries an `index.md` at its root and at",
      "each `{internal,external}` partition so Starlight renders a clean",
      "sidebar:",
      "",
      "- `<meetingsRoot>/transcripts/index.md` (and one per partition)",
      "- `<meetingsRoot>/notes/index.md` (and one per partition)",
      "- `<meetingsRoot>/insights/index.md` (and one per partition)",
      "",
      "Phase 1 must create the matching `insights/{type}/index.md` page",
      "the first time it writes a file into that partition. Phase 2 must",
      "create the matching `notes/{type}/index.md` page the first time it",
      "writes a notes file into that partition. Follow the `section-index`",
      "convention: a 1–2 paragraph summary of the tree's purpose plus a",
      "linked listing of the partition's children. Append a row for the",
      "current meeting's basename when an index already exists.",
      "",
      "---",
      "",
      "## Meeting type handling",
      "",
      "Meeting frontmatter is expected to carry a `meeting_type` field whose",
      "value is one of the project's concrete meeting-type `id`s (declared",
      "via `AgentConfigOptions.meetings.meetingTypes`). Each concrete `id`",
      "maps to one of six generic **meeting kinds** that drive type-specific",
      "extraction rules:",
      "",
      "| Kind | Primary outputs | Phase-1/2 rules |",
      "|------|-----------------|-----------------|",
      "| `planning` | Sprint/plan updates, task issues, requirement drafts | Extract every task assignment. Phase 4 updates the active sprint-plan doc directly and creates one issue per assigned task. |",
      "| `review` | Retro notes, follow-up issues, status updates, requirement revisions | Mark completed tasks in the active sprint-plan doc. Capture retrospective learnings inside the meeting notes — do not spawn a separate retro document. |",
      "| `brainstorm` | Future-feature candidates, research topics, very few Firm decisions | Lower the bar for Open Questions. Do **not** create requirement or ADR issues from brainstorm output unless the item is explicitly marked **Firm**. Prefer research/discovery issues for promising ideas. |",
      "| `standup` | Action items, blockers, status updates | Do not produce requirement, ADR, or BDR drafts. Phase 3 (Draft) is almost always skipped. Capture action items in the notes `## Action Items` table; file issues only for agent-workable doc deliverables (per the **Action-item filing policy**) and apply sprint-plan status updates. |",
      "| `external` | Customer/competitive intel, people/company profiles, lead capture | Default every attendee outside the team into **People of Interest**. Default every organization mentioned into **Companies of Interest** (signal threshold still applies). Capture stated customer pain points as candidate **BR** (business requirements), not FR. |",
      "| `other` | General meeting extraction | Apply the default workflow with no type-specific overrides. |",
      "",
      "**How to resolve the kind:**",
      "",
      "1. Read `meeting_type` from the frontmatter.",
      "2. Look up the matching `id` in the project's **Recognized meeting",
      "   types** table (rendered below when `meetingTypes` is declared).",
      "3. Use the `Kind` column value. If the table is absent, the `id` is",
      "   unknown, or the `Kind` column is `—`, fall back to `other`.",
      "4. Apply the rules in the row above **in addition to** the normal",
      "   phase workflow — never as a replacement.",
      "",
      "If `meeting_type` is missing, invalid, or the declared `id` is not in",
      "the project's table, note it in the extraction's Open Questions and",
      "proceed with the default workflow (treat as `other`).",
      "",
      "## Areas filtering",
      "",
      "Meeting frontmatter may carry an `areas: [...]` list whose values",
      "match `id`s from the project's **Area → doc-root mapping** table",
      "(declared via `AgentConfigOptions.meetings.meetingAreas`). The list",
      "scopes phase-4 direct edits and cross-references to the doc-root",
      "sub-folders registered for those areas. A given area's effective",
      "path resolves as `<docsRoot>/<docRoot>` — `docsRoot` comes from",
      "`AgentPathsConfig.docsRoot`; `docRoot` is the sub-folder shown in",
      "the **Area → doc-root mapping** table.",
      "",
      "**Gating contract (phase 4 only):**",
      "",
      "- **Scoped in:** direct edits and cross-reference stubs under the",
      "  `docRoot` of every `id` present in the meeting's `areas` list.",
      "- **Scoped out:** direct edits to any doc-root sub-folder that is",
      "  declared in the project's area map but whose `id` is **not** in",
      "  the meeting's `areas`. For those out-of-scope areas, create",
      "  follow-up issues instead of editing the doc directly.",
      "- **Not gated:** issue creation (requirements, ADRs, profiles,",
      "  research) is always in scope regardless of areas.",
      "",
      "**Resolution rules:**",
      "",
      "1. If the meeting's `areas` frontmatter is missing or empty **and**",
      "   the project has declared a non-empty `meetingAreas` map, no",
      "   doc-root sub-folder is gated in: direct edits to any declared",
      "   `docRoot` sub-folder are out-of-scope and become follow-up issues",
      "   per step 6. Only the default meetings root (`meetingsRoot`)",
      "   remains editable.",
      "2. If `areas` lists an `id` that is not in the project's area map,",
      "   note it in the extraction's Open Questions and treat the unknown",
      "   `id` as absent. Do **not** invent a doc-root path for it.",
      "3. Out-of-scope direct edits become `type:docs` follow-up issues on",
      "   phase 4 step 6, with the area noted in the issue body.",
      "",
      "When the project has **not** declared any `meetingAreas` at all,",
      "this section applies degenerately — no areas are gated, no",
      "follow-up issues are synthesized for missing areas, and phase-4",
      "direct edits follow the default workflow (step 6 edits any doc",
      "flagged as Firm, unrestricted by `docRoot`).",
      "",
      "---",
      "",
      "## Action-item filing policy",
      "",
      "Not every action item should become a GitHub issue. The pipeline",
      "can only ever close issues whose deliverable an automated agent",
      "(or this repo's existing channels) can produce. Filing an issue",
      "for a task only a human can do — in the real world or in a system",
      "this pipeline cannot touch — creates queue noise that no worker or",
      "orchestrator can ever resolve. Apply the **agent-workability",
      "test** below in phases 1, 3, and 4.",
      "",
      "### Agent-workability test",
      "",
      "An action item is **agent-workable** only when completing it",
      "produces a documentation deliverable an automated agent can author",
      "in this repo's docs tree — a requirement document, an ADR, a docs",
      "page, a BCM capability model, a research note, or a roadmap /",
      "product-doc update. Route agent-workable items through their",
      "**dedicated downstream channel** (`req:write`, `docs:write`,",
      "`bcm:*`, `research:scope`, roadmap/product-doc follow-up). Never",
      "file them as a generic `action-item` / `type:chore` issue.",
      "",
      "An action item is **human-owned** when it requires a person to act",
      "in the real world or in a system this pipeline cannot reach.",
      "Typical cues:",
      "",
      '- **send / email / schedule / invite / call** — e.g. "send out',
      '  company invoices", "invite X to next week\'s call".',
      '- **install / configure / provision / set up** — e.g. "install and',
      '  configure Claude", "get codebase read access".',
      "- **decide / approve / choose / prioritise** (a human judgement",
      "  call, not a documented decision record).",
      '- **communicate / notify / give a heads-up** — e.g. "give Dr. Y a',
      '  heads-up".',
      "- **get / request access or credentials**, **pay / invoice**.",
      "- **build or change code in another repo**, **design in an",
      "  external tool** (Figma, etc.).",
      "",
      "Record human-owned items **only** in the notes `## Action Items`",
      "table (who / what / when) — that table is the **system of record**",
      "for human follow-ups. Do **not** file a GitHub issue for them.",
      "",
      "When an item is ambiguous, default to **human-owned**",
      "(notes-only): a missed issue is cheaper than queue noise the",
      "pipeline can never close.",
      "",
      "**Consumer tuning.** When the project supplies",
      "`AgentConfigOptions.meetings.actionItemFiling`, an",
      "**Action-item filing policy** subsection is rendered into the",
      "meetings taxonomy with any extra human-owned / agent-workable cues",
      "and a note when the split is disabled. Apply those overrides on",
      "top of the built-in cues above; if the split is disabled, fall",
      "back to filing a follow-up issue for every non-draft action item.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_MAINTAINER_SECTION,
      "## Traceability",
      "",
      "All outputs must be bi-directionally linked so any artifact can be traced",
      "back to the meeting that produced it and forward to everything it spawned.",
      "",
      "### Backward links (created artifact → meeting source)",
      "",
      "Every GitHub issue and document created by this pipeline must include a",
      "`## Traceability` section with:",
      "",
      "```markdown",
      "## Traceability",
      "",
      "- **Source meeting:** <path to transcript or meeting notes>",
      "- **Extraction:** <path to extraction file>",
      "- **Phase issue:** #<N> (the phase issue that created this artifact)",
      "```",
      "",
      "### Forward links (meeting source → created artifacts)",
      "",
      "After Phase 4 (Link) creates all follow-up issues and documents:",
      "",
      "1. **Update the extraction file** with a `## Downstream Artifacts` section",
      "   listing every issue and document created from this meeting:",
      "",
      "   ```markdown",
      "   ## Downstream Artifacts",
      "",
      "   | Artifact | Type | Issue/Path |",
      "   |----------|------|------------|",
      "   | <title> | requirement / ADR / docs / profile / research | #<N> or <path> |",
      "   ```",
      "",
      "2. **Update the meeting notes** with a similar `## Related Issues` section",
      "   listing all issues created from this meeting.",
      "",
      "3. **Comment on the extract issue** with a summary linking to all created",
      "   artifacts.",
      "",
      "### Within-pipeline links",
      "",
      "- Phase issues link to predecessors via `Depends on: #N`",
      "- Phase issues reference the extraction file path in their body",
      "- Draft documents reference both the extraction and the meeting notes",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      ...renderIssueClosePolicySection(),
      "",
      "---",
      "",
      "## Phase 1: Extract (`meeting:extract`)",
      "",
      "**Goal:** Read the available meeting inputs (notes, transcript, or both)",
      "and categorize all substantive content into a structured extraction.",
      "",
      "### Steps",
      "",
      "1. Identify the meeting's basename and `{internal,external}` partition",
      "   from the issue body using the **basename-pairing algorithm**, then",
      "   read **every** sibling file that exists on disk:",
      "   - Notes: `<meetingsRoot>/notes/{type}/<basename>.md`",
      "   - Transcript: `<meetingsRoot>/transcripts/{type}/<basename>.md`",
      "",
      "   **Basename-pairing algorithm.** The issue body (or invoking",
      "   skill) may name either tree's path; resolve the sibling",
      "   implicitly:",
      "",
      "   - Infer the basename (filename without `.md`) and the",
      "     `{internal,external}` partition from the provided path —",
      "     `…/notes/internal/2026-03-14-sprint-kickoff.md` yields",
      "     basename `2026-03-14-sprint-kickoff` and type `internal`.",
      "   - If the input path lives under `/notes/`, look for the",
      "     sibling at `<meetingsRoot>/transcripts/<type>/<basename>.md`.",
      "     If it lives under `/transcripts/`, look for the sibling at",
      "     `<meetingsRoot>/notes/<type>/<basename>.md`. If the path",
      "     matches neither (ad-hoc location), infer basename + type as",
      "     best you can from the filename and surrounding folder",
      "     segments, then proceed with whichever sibling-tree lookups",
      "     succeed. Best-effort, never fail-loud.",
      "   - A missing sibling is a normal outcome — the input-case matrix",
      "     covers notes-only and transcript-only paths. Never prompt or",
      "     warn the user about the absence.",
      "",
      "   Use the **input-case matrix** in **Meeting file layout** to decide",
      "   which case you are in (both / notes-only / transcript-only). Parse",
      "   the notes frontmatter first (if a notes file exists), capturing",
      "   `meeting_type`, `areas`, `source`, and `confidence`. Resolve",
      "   `meeting_type` to a kind using the **Meeting type handling**",
      "   table; note the resolved kind in the extraction frontmatter. If",
      "   `meeting_type` is missing, treat it as `other` and flag it in",
      "   Open Questions — do **not** fail-loud on missing frontmatter.",
      "2. Identify and categorize content into these buckets:",
      "",
      "   | Bucket | What to look for |",
      "   |--------|-----------------|",
      '   | **Decisions** | "We decided...", "Let\'s go with...", explicit choices |',
      '   | **Requirements** | Feature descriptions, acceptance criteria, "it should..." |',
      "   | **Technology discussions** | Platform comparisons, architecture options, tool evaluations |",
      '   | **Action items** | "[Person] will...", "Next step is...", assigned tasks. Tag each with its **disposition** (`agent-workable` vs `human-owned`) per the **Action-item filing policy**. |',
      '   | **Open questions** | "We need to figure out...", unresolved debates |',
      "   | **People of interest** | Industry contacts, domain experts mentioned |",
      "   | **Companies of interest** | Competitors, vendors, partners discussed |",
      "   | **Strategic direction** | Business model changes, market positioning |",
      "   | **Product direction** | Roadmap changes, feature prioritization |",
      "",
      "3. Write the extraction to",
      "   `<meetingsRoot>/insights/{type}/<basename>.md` (matching the",
      "   basename and `{internal,external}` partition of the source",
      "   notes / transcript) with structured sections:",
      "   - Attendees",
      "   - Decisions Made (with category and confidence: Firm / Tentative / Needs confirmation)",
      "   - Requirements Identified (with category and priority estimate)",
      "   - Technology Discussions (with status: Decided / Leaning toward / Open)",
      "   - Action Items (with assignee and due date if stated, and a",
      "     **disposition** tag — `agent-workable` or `human-owned` — per",
      "     the **Action-item filing policy** so Phase 4 can route each",
      "     item without re-classifying it)",
      "   - Open Questions",
      "   - People of Interest (with context and whether a profile exists)",
      "   - Companies of Interest (with type and context)",
      "   - Strategic / Product Direction",
      "",
      "   Carry the following into the extraction frontmatter so downstream",
      "   phases can gate behaviour without re-parsing the source files:",
      "   `meeting_type`, the resolved `meeting_kind`, `areas`,",
      "   `transcript_available: true|false` (cached from the filesystem",
      "   check in step 1), and `confidence: high|medium|low` (per the",
      "   input-case matrix — `high` when a transcript is present, `medium`",
      "   on notes-only, never silently downgraded to `low` without an",
      "   explicit reason recorded in Open Questions).",
      "",
      "4. **Apply type-specific rules.** Consult the **Meeting type handling**",
      "   table for the resolved kind and apply its phase-1 rules in",
      "   addition to the default extraction above. Examples:",
      "   - `standup`: skip requirement, ADR, and BDR extraction; focus on",
      "     action items and status.",
      "   - `brainstorm`: emphasize Open Questions and Future Features.",
      "     Only mark decisions as Firm when the transcript is unambiguous.",
      "   - `external`: default attendees outside the team into People of",
      "     Interest; default organizations mentioned into Companies of",
      "     Interest (signal threshold still applies); capture customer",
      "     pain points as candidate BR (not FR).",
      "",
      "5. **Plan the `insights/` tree index update — do not commit it",
      "   yet.** Ensure `<meetingsRoot>/insights/index.md` and",
      "   `<meetingsRoot>/insights/{type}/index.md` exist (create them",
      "   following the `section-index` convention if missing). Note",
      "   the basename row this phase will eventually append, but",
      "   **defer** the row insert and its commit to step 8 below so",
      "   the shared-index commit can land in the smallest possible",
      "   window before push.",
      "",
      "6. **Create downstream phase issues** using `gh issue create`:",
      "   - Always create a `meeting:notes` issue (blocked on this extract issue)",
      "   - If requirements OR decisions/ADRs identified, create a `meeting:draft` issue",
      "     (blocked on the notes issue). For kind `standup`, skip the",
      "     `meeting:draft` issue — standup meetings almost never produce",
      "     formal drafts.",
      "   - Always create a `meeting:link` issue — blocked on the draft issue if one",
      "     was created, otherwise blocked on the notes issue",
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "7. **Commit the extraction content first.** Stage and commit",
      "   the `insights/{type}/<basename>.md` file (and any newly",
      "   created `insights/index.md` / `insights/{type}/index.md`",
      "   stub pages from step 5 that do not yet exist on the remote)",
      "   in a single focused commit. **Do not push yet.**",
      "",
      "8. **Defer the shared-index row insert to a final pre-push",
      "   commit.** Per the `shared-editing-safety` rule's",
      "   **Defer Shared-Index Commit to Final Pre-Push Step**",
      "   subsection, the index update for an existing",
      "   `insights/{type}/index.md` is a **shared-index row insert**",
      "   that races other parallel meeting sessions writing rows into",
      "   the same partition file. Apply the deferred sequence:",
      "",
      "   ```bash",
      "   git fetch origin",
      "   git pull --rebase origin <default-branch>",
      "   ```",
      "",
      "   Re-read `insights/{type}/index.md` from the now-up-to-date",
      "   working tree, re-compute the alphabetical insert position",
      "   for the current meeting's basename row (another session may",
      "   have appended a sibling row in the meantime), insert exactly",
      "   one row, and commit the index edit in its **own focused",
      "   commit** whose only file is the shared index. Run the",
      "   commit-path verification step (`git show HEAD:<index-path>`",
      "   + grep count) against that commit before pushing.",
      "",
      "9. **Push and close.** Push the branch, then close the extract issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 2: Notes (`meeting:notes`)",
      "",
      "**Goal:** Produce a curated `notes/{type}/<basename>.md` file that",
      "reflects the structured meeting record. The pivot here from older",
      'versions of this bundle: Phase 2 is no longer "author notes from',
      'scratch." It is **enhance and verify** — restructure the existing',
      "Gemini-generated notes (when present), verify factual claims",
      "against the transcript (when present), and fall back to authoring",
      "only when no notes file exists.",
      "",
      "### Steps",
      "",
      "1. Read the extraction at",
      "   `<meetingsRoot>/insights/{type}/<basename>.md` (output of Phase 1).",
      "   Read the `transcript_available` and `confidence` fields from its",
      "   frontmatter. Then determine which input-case branch applies by",
      "   checking the filesystem for the sibling notes and transcript",
      "   files.",
      "2. **Branch by input case** (see the input-case matrix in the",
      "   **Meeting file layout** section):",
      "",
      "   - **Both notes and transcript present** (enhance + verify):",
      "     Read the existing `notes/{type}/<basename>.md` in place.",
      "     Restructure it into the canonical section layout below.",
      "     Verify every factual claim against the transcript — when the",
      "     transcript contradicts the notes, the transcript wins and the",
      "     notes are corrected. Fill in gaps the transcript reveals were",
      "     missed. Overwrite the notes file in place; preserve the",
      "     existing frontmatter and update only the fields you changed",
      "     (e.g. flip `source: gemini` to `source: hybrid` once the agent",
      "     has edited the file). Set `confidence: high`.",
      "",
      "   - **Notes only** (light structural cleanup):",
      "     Read the existing `notes/{type}/<basename>.md`. Restructure it",
      "     into the canonical section layout below. Do **not** invent",
      "     dialogue or claims the notes do not record. Do **not** mark",
      "     `confidence: high` — keep `confidence: medium` and record the",
      '     reason ("no transcript available for verification") in the',
      "     notes' Open Questions section so downstream phases know to",
      "     treat downstream requirement drafts as `Tentative`.",
      "",
      "   - **Transcript only** (author from scratch):",
      "     The notes file does not yet exist. Author",
      "     `notes/{type}/<basename>.md` from the transcript using the",
      "     section layout below. Populate frontmatter with",
      "     `source: gemini` if the transcript is a Gemini transcript,",
      "     otherwise `source: unknown`. Set `confidence: high`.",
      "",
      "3. Canonical notes section layout (applied in every branch above):",
      "   - Meeting metadata (title, date, attendees)",
      "   - Agenda / topics covered",
      "   - Key Discussion Points (organized by topic)",
      "   - Decisions (numbered, with rationale)",
      "   - Action Items (table: who, what, when). This table is the",
      "     **system of record for human follow-ups** — human-owned",
      "     action items live here and are never filed as GitHub issues",
      "     (see the **Action-item filing policy**). Capture every",
      "     action item here regardless of disposition.",
      "   - Open Questions",
      "   - Follow-up items",
      "4. **Plan the `notes/` tree index update — do not commit it",
      "   yet.** Ensure `<meetingsRoot>/notes/index.md` and",
      "   `<meetingsRoot>/notes/{type}/index.md` exist (create them",
      "   following the `section-index` convention if missing). Note",
      "   the basename row this phase will eventually append, but",
      "   **defer** the row insert and its commit to step 6 below so",
      "   the shared-index commit can land in the smallest possible",
      "   window before push.",
      "5. **Commit the notes content first.** Stage and commit the",
      "   `notes/{type}/<basename>.md` file (and any newly created",
      "   `notes/index.md` / `notes/{type}/index.md` stub pages from",
      "   step 4 that do not yet exist on the remote) in a single",
      "   focused commit. **Do not push yet.**",
      "6. **Defer the shared-index row insert to a final pre-push",
      "   commit.** Per the `shared-editing-safety` rule's",
      "   **Defer Shared-Index Commit to Final Pre-Push Step**",
      "   subsection, the index update for an existing",
      "   `notes/{type}/index.md` is a **shared-index row insert**",
      "   that races other parallel meeting sessions writing rows",
      "   into the same partition file. Apply the deferred sequence:",
      "",
      "   ```bash",
      "   git fetch origin",
      "   git pull --rebase origin <default-branch>",
      "   ```",
      "",
      "   Re-read `notes/{type}/index.md` from the now-up-to-date",
      "   working tree, re-compute the alphabetical insert position",
      "   for the current meeting's basename row (another session may",
      "   have appended a sibling row in the meantime), insert exactly",
      "   one row, and commit the index edit in its **own focused",
      "   commit** whose only file is the shared index. Run the",
      "   commit-path verification step against that commit before",
      "   pushing.",
      `7. Push, then close the notes issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 3: Draft (`meeting:draft`)",
      "",
      "**Goal:** Draft proposals for requirements, ADRs, and product/strategy updates.",
      "",
      "This phase only exists if the extraction identified requirements, architectural",
      "decisions, or strategy changes. If this issue exists, execute it.",
      "",
      "Draft proposals only for **agent-workable** deliverables (per the",
      "**Action-item filing policy**). Action items tagged `human-owned`",
      "in the extraction are not drafted here — they stay in the notes",
      "`## Action Items` table and produce no follow-up issue.",
      "",
      "### Steps",
      "",
      "1. Read the extraction file from Phase 1 at",
      "   `<meetingsRoot>/insights/{type}/<basename>.md`.",
      "2. Check existing requirement registries and ADR registries for duplicates.",
      "3. Draft requirement proposals with:",
      "   - Category (FR, BR, NFR, etc.)",
      "   - Summary (2-3 sentences)",
      "   - Draft acceptance criteria",
      "   - Related existing requirements",
      "4. Draft ADR proposals for technology decisions with:",
      "   - Context and problem statement",
      "   - Options discussed",
      "   - Stated preferences or decisions",
      "   - Status: Proposed or Decided",
      "5. Summarize product/strategy document updates needed.",
      "6. Commit, push, and close the draft issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 4: Link (`meeting:link`)",
      "",
      "**Goal:** Create GitHub issues for follow-up work, cross-reference the",
      "meeting into existing documentation, and complete bi-directional traceability.",
      "",
      "### Idempotent dedup gate (open AND closed)",
      "",
      "Phase 3 (`meeting:draft`) already files one downstream issue per",
      "drafted artifact — a `req:write` per requirement draft, plus",
      "`docs:write` / `bcm:*` / `research:scope` where applicable. Phase 4",
      "must **not** re-file what Phase 3 already filed. Before creating",
      "**any** downstream issue (`req:write`, `docs:write`, `bcm:*`,",
      "`research:scope`) for a drafted artifact, dedup against existing",
      "issues:",
      "",
      "1. **Search open AND closed issues** for one already covering the",
      "   same artifact, matching on the draft's title / basename — e.g.",
      "   `gh issue list --search '<draft title or basename>' --state all --json number,title,state`.",
      "   Searching **all states** is load-bearing: the original may have",
      "   already been worked and **merged (closed)**, and an open-only",
      "   search misses it — re-filing then collides with the shipped",
      "   document and produces an ID-collision PR.",
      "2. **If a match exists (open or closed), SKIP creation.** Link to",
      "   the existing issue from the `## Downstream Artifacts` section",
      "   instead of filing a new one.",
      "3. **Only create issues for follow-up work that has no Phase 3",
      "   draft.** A drafted artifact already has its downstream issue;",
      "   file new issues only for items the draft phase did not cover.",
      "4. **Be idempotent.** Re-running the same `meeting:link` issue must",
      "   file nothing new — a second run finds the first run's issues",
      "   (now open or closed) via the same search and skips them.",
      "",
      "Apply this gate to **both** Step 2 (requirement issues) and Step 4",
      "(action-item routing) below.",
      "",
      "### Steps",
      "",
      ...renderIssueTypeAssignmentBlanket(),
      "",
      "1. Read the drafts from Phase 3 (if they exist) and the extraction",
      "   from Phase 1 at `<meetingsRoot>/insights/{type}/<basename>.md`.",
      "   Re-read the extraction frontmatter for `meeting_kind` and `areas`,",
      "   then build the area-gate: the set of `docRoot` sub-folders that",
      "   are in scope for direct edits on this meeting. Apply the rules in",
      "   the **Areas filtering** section above.",
      "2. Create requirement issues using `gh issue create` with appropriate labels.",
      "   **First apply the Idempotent dedup gate above:** search open AND",
      "   closed issues for one already covering the drafted requirement",
      "   (match on its title / basename) and **skip creation when a match",
      "   exists** — Phase 3 already filed a `req:write` issue per requirement",
      "   draft, so file here only for requirements with no Phase 3 draft.",
      "   Include a `## Traceability` section in each issue body linking back to",
      "   the source meeting and extraction file. Issue creation is **not**",
      "   gated by areas.",
      "3. Create ADR issues if technology decisions need formal records.",
      "   Include a `## Traceability` section in each. Not gated by areas.",
      "4. **Route action items per the Action-item filing policy — do",
      "   not file a generic `action-item` issue per task.** Read each",
      "   action item's `disposition` tag from the extraction (re-apply",
      "   the **agent-workability test** if the tag is missing):",
      "   - **Agent-workable** (yields a doc deliverable an agent can",
      "     author): file it through its **dedicated downstream channel**",
      "     — a `req:write` issue for a requirement, a `docs:write` issue",
      "     for a docs page, `bcm:*` for a capability model, a",
      "     `research:scope` issue for a research note, or a roadmap /",
      "     product-doc follow-up. **First apply the Idempotent dedup gate",
      "     above:** search open AND closed issues for one already covering",
      "     this artifact and **skip creation when a match exists** (link",
      "     to it instead) — file only for action items that have no Phase",
      "     3 draft. Use the matching template from the issue-templates",
      "     page; include a `## Traceability` section. Issue creation is",
      "     not gated by areas.",
      "   - **Human-owned** (send/schedule/install/decide/communicate/",
      "     get-access/build-elsewhere): record it **only** in the notes",
      "     `## Action Items` table (step 8 already carries the table",
      "     forward). **File no GitHub issue.** When ambiguous, treat the",
      "     item as human-owned.",
      "   If the project disabled the split via",
      "   `meetings.actionItemFiling.enabled: false`, fall back to filing",
      "   a follow-up issue for every action item not already covered by",
      "   a Phase 3 draft.",
      "5. Cross-reference the meeting in any existing documents that were",
      "   discussed **and that live under an in-scope `docRoot`**. For",
      "   discussed documents under an out-of-scope `docRoot`, open a",
      "   follow-up `type:docs` issue instead of editing the doc directly.",
      "6. Apply direct product/strategy doc updates for items flagged as",
      "   **Firm** confidence. When the project declares `meetingAreas`,",
      "   gate these edits: only apply them when the target path lies",
      "   under an in-scope `docRoot`, and for Firm items targeting an",
      "   out-of-scope `docRoot` open a follow-up `type:docs` issue",
      "   noting the area and the target path. When the project has not",
      "   declared any `meetingAreas`, apply Firm updates unrestricted",
      "   (the degenerate case above). For the kind `planning`, also",
      "   update the active sprint-plan doc directly (when the",
      "   sprint-plan doc's area is in-scope, or always when no",
      "   `meetingAreas` are declared); for the kind `review`, mark",
      "   completed tasks in that same doc.",
      "7. **Update the extraction file** at",
      "   `<meetingsRoot>/insights/{type}/<basename>.md` with a",
      "   `## Downstream Artifacts` section listing every issue and",
      "   document created from this meeting. Note any items that were",
      "   deferred to follow-up issues because of the area gate.",
      "8. **Update the meeting notes** at",
      "   `<meetingsRoot>/notes/{type}/<basename>.md` with a",
      "   `## Related Issues` section listing all issues created from this",
      "   meeting.",
      "9. Comment on the parent extract issue with a summary linking to all",
      "   created artifacts.",
      "10. Commit and push (if any file changes were made), then close the",
      `    link issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## GitHub Integration",
      "",
      "- Use `gh` CLI for all GitHub operations",
      "- Apply the appropriate `meeting:*` phase label to each phase issue",
      "- Use `type:docs` for documentation-producing issues, `type:chore` for",
      "  maintenance/organizational tasks the pipeline can action",
      "- Do **not** file issues for human-owned action items — per the",
      "  **Action-item filing policy** they live only in the notes",
      "  `## Action Items` table",
      "- Use `status:` labels to track progress (`status:ready`, `status:in-progress`, `status:done`)",
      "- Reference the source meeting transcript in all created issues",
      "- Link phase issues with `Depends on: #N` to enforce ordering",
      "",
      "## When to Shorten the Pipeline",
      "",
      "- **Short meetings** (<30 min, <2000 words): combine extract + notes into one session",
      "- **No requirements or decisions**: Phase 1 skips creating the `meeting:draft` issue",
      "- **Only action items**: extract + notes + link (3 phase issues)",
    ].join("\n"),
  };
}

/** Skill that kicks off meeting transcript processing. */
const processMeetingSkill: AgentSkill = {
  name: "process-meeting",
  description:
    "Process a meeting transcript through the 4-phase meeting analysis pipeline",
  disableModelInvocation: true,
  userInvocable: true,
  context: "fork",
  agent: "meeting-analyst",
  platforms: { cursor: { exclude: true } },
  instructions: [
    "# Process Meeting",
    "",
    "Kick off meeting processing by executing Phase 1 (Extract) and",
    "creating downstream phase issues for the remaining phases. Inputs",
    "live in the **sibling-tree layout**: pass any file from",
    "`<meetingsRoot>/notes/{type}/` or `<meetingsRoot>/transcripts/{type}/`",
    "(both, notes-only, and transcript-only are all valid input cases).",
    "",
    "## Usage",
    "",
    "/process-meeting <path-to-notes-or-transcript>",
    "",
    "## Basename-pairing algorithm",
    "",
    "The user may pass either a notes-tree path or a transcripts-tree",
    "path. Resolve the sibling implicitly via filesystem lookup —",
    "do **not** prompt the user or fail-loud when the sibling is",
    "absent:",
    "",
    "1. Infer the **basename** (filename without the `.md` extension)",
    "   and the `{internal,external}` partition from the provided path.",
    "   Both segments are usually adjacent in the path —",
    "   `…/notes/internal/2026-03-14-sprint-kickoff.md` yields basename",
    "   `2026-03-14-sprint-kickoff` and type `internal`.",
    "2. Determine the **input tree** from the path:",
    "   - If the path contains `/notes/`, treat it as the notes input",
    "     and look for the sibling at",
    "     `<meetingsRoot>/transcripts/<type>/<basename>.md`.",
    "   - If the path contains `/transcripts/`, treat it as the",
    "     transcript input and look for the sibling at",
    "     `<meetingsRoot>/notes/<type>/<basename>.md`.",
    "   - If the path matches neither (e.g. an ad-hoc location), infer",
    "     basename + type as best you can from the filename and",
    "     surrounding folder segments, then proceed with whichever",
    "     sibling-tree lookups succeed. Best-effort, never fail-loud.",
    "3. Read **every** sibling that exists on disk. The combination",
    "   (notes present, transcript present, both present) selects the",
    "   Phase-1 input case from the **input-case matrix** in the",
    "   `meeting-analyst` agent prompt.",
    "4. A missing sibling is a normal outcome — proceed with whatever",
    "   was found. Notes-only and transcript-only are both valid",
    "   Phase-1 input cases; the agent never warns or prompts the",
    "   user about the absence.",
    "",
    "## Steps",
    "",
    "1. Resolve the basename, partition, and any sibling files using",
    "   the **Basename-pairing algorithm** above",
    "2. Execute Phase 1 (Extract) — categorize the available content into",
    "   decisions, requirements, action items, open questions, and more.",
    "   Tag each action item's disposition (`agent-workable` vs",
    "   `human-owned`) per the agent's **Action-item filing policy** so",
    "   Phase 4 keeps human-owned tasks out of the issue queue",
    "3. Write the extraction to",
    "   `<meetingsRoot>/insights/{type}/<basename>.md`",
    "4. Create downstream phase issues using `gh issue create`:",
    "   - `meeting:notes` issue (always)",
    "   - `meeting:draft` issue (if requirements or decisions were found)",
    "   - `meeting:link` issue (always)",
    "",
    ...renderIssueTypeAssignmentStep(),
    "5. Each downstream issue includes `Depends on:` linking to its predecessor",
    "6. Commit and push the extraction file",
    "",
    "## Input",
    "",
    "Provide a path to a file in either `notes/` or `transcripts/`. The",
    "agent will look up the sibling automatically via basename match. A",
    "missing sibling is a normal case (notes-only or transcript-only) —",
    "the agent handles all three input cases.",
    "",
    "## Output",
    "",
    "- A structured extraction at",
    "  `<meetingsRoot>/insights/{type}/<basename>.md`",
    "- Phase issues with `meeting:*` labels for downstream agent sessions",
    "  to pick up (notes, draft, link)",
  ].join("\n"),
};

/**
 * Build the meeting-analysis bundle with the supplied default sub-agent
 * model tier. The tier knob lets consumers globally demote the
 * `meeting-analyst` sub-agent to BALANCED (sonnet) — which is the
 * post-2026-05-08 default — without forking the bundle.
 */
export function buildMeetingAnalysisBundle(
  tier: AgentModel = AGENT_MODEL.BALANCED,
): AgentRuleBundle {
  return {
    name: "meeting-analysis",
    description:
      "Meeting transcript processing workflow with 4-phase pipeline (extract, notes, draft, link)",
    appliesWhen: () => true,
    rules: [
      {
        name: "meeting-processing-workflow",
        description:
          "Describes the 4-phase meeting processing pipeline, extraction taxonomy, and labeling conventions",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Meeting Processing Workflow",
          "",
          "Use `/process-meeting <path>` to process a meeting through a",
          "4-phase pipeline (extract → notes → draft → link). Each phase runs as a",
          "separate agent session tracked by a GitHub issue with a `meeting:*` label.",
          "See the `meeting-analyst` agent definition for full workflow details.",
          "",
          "Meeting artifacts live in **three parallel trees** rooted at the",
          "project's meetings root:",
          "",
          "- `transcripts/{internal,external}/` — verbatim transcripts (evidence)",
          "- `notes/{internal,external}/` — curated narrative summary",
          "  (input + Phase 2 output)",
          "- `insights/{internal,external}/` — structured extraction (Phase 1 output)",
          "",
          "The three trees share identical basenames so a meeting's siblings",
          "are looked up by filename, not by frontmatter cross-references.",
          "Phase 1 accepts every input case (notes + transcript, notes only,",
          "or transcript only) and Phase 2 enhances the notes file in place",
          "rather than authoring it from scratch.",
          "",
          "Notes and insights files may carry optional frontmatter fields",
          "`slug`, `source`, `transcript_available`, and `confidence`",
          "alongside the existing `meeting_type` and `areas` fields. Missing",
          "frontmatter is treated as a normal input case — never fail-loud.",
          "",
          "Meeting notes may declare a `meeting_type` (one of the project's",
          "recognized types) and an `areas: [...]` list. The `meeting_type`",
          "resolves to a generic kind — `planning` / `review` / `brainstorm` /",
          "`standup` / `external` / `other` — that drives type-specific",
          "extraction rules in phases 1–2. The `areas` list scopes phase-4",
          "direct edits to the doc-root sub-folders declared in the project's",
          "**Area → doc-root mapping** table. When both fields are absent",
          "the pipeline falls back to the default workflow (kind `other`,",
          "no area gating).",
          "",
          "Phase 4 applies an **agent-workability split** to action items:",
          "items that yield a documentation deliverable an agent can author",
          "are filed through their dedicated downstream channel",
          "(`req:write`, `docs:write`, `bcm:*`, `research:scope`, roadmap",
          "follow-up); human-owned tasks (send / schedule / install /",
          "decide / communicate / get-access / build-elsewhere) are",
          "recorded **only** in the notes `## Action Items` table, which is",
          "the **system of record for human follow-ups** — no GitHub issue",
          "is filed for them. Consumers tune or disable the split via",
          "`AgentConfigOptions.meetings.actionItemFiling`.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [processMeetingSkill],
    subAgents: [buildMeetingAnalystSubAgent(tier)],
    labels: [
      {
        name: "type:meeting-processing",
        color: "5319E7",
        description: "Meeting transcript processing pipeline",
      },
      {
        name: "meeting:extract",
        color: "C5DEF5",
        description: "Phase 1: raw extraction from a meeting transcript",
      },
      {
        name: "meeting:notes",
        color: "BFDADC",
        description: "Phase 2: curated notes derived from an extraction",
      },
      {
        name: "meeting:draft",
        color: "D4C5F9",
        description: "Phase 3: draft follow-up issues proposed from notes",
      },
      {
        name: "meeting:link",
        color: "FEF2C0",
        description:
          "Phase 4: linking/reconciling drafted issues with existing work",
      },
    ],
  };
}

/**
 * Default-tier instance of the meeting-analysis bundle, preserved for
 * backward compatibility with consumers that import the const directly.
 * The factory above is the canonical entry point when a consumer
 * supplies `AgentConfigOptions.defaultAgentTier`.
 */
export const meetingAnalysisBundle: AgentRuleBundle =
  buildMeetingAnalysisBundle();
